import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { FormikInputField } from 'components/Form/FormikInputField/FormikInputField'
import { autocompleteBankName } from 'services/endpoints/settings/general/payout'
import colors from 'styles/colors'

type BankAutocompleteItem = {
    bank_name: string
    country_code: string
}

interface BankNameAutocompleteFieldProps {
    name: string
    label: string
    value: string
    country?: string
    onValueChange: (value: string) => void
    onSelect: (bank: BankAutocompleteItem) => void
    customClass?: string
    showRequiredErrors?: boolean
}

type DropdownPlacement = 'bottom' | 'top'

type DropdownPosition = {
    top?: number
    bottom?: number
    left: number
    width: number
    maxHeight: number
    placement: DropdownPlacement
}

const GAP_PX = 8
const VIEWPORT_PADDING = 12
const DEFAULT_MAX_HEIGHT = 280

export const BankNameAutocompleteField = ({
    name,
    label,
    value,
    country,
    onValueChange,
    onSelect,
    customClass,
    showRequiredErrors = true
}: BankNameAutocompleteFieldProps) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    const [query, setQuery] = useState(value || '')
    const [results, setResults] = useState<BankAutocompleteItem[]>([])
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)
    const [dropdownPosition, setDropdownPosition] =
        useState<DropdownPosition | null>(null)

    const normalizedCountry = useMemo(() => {
        const raw = (country || '').trim().toLowerCase()

        if (!raw) return undefined
        if (raw === 'canada' || raw === 'ca') return 'CA'
        if (
            raw === 'united states' ||
            raw === 'usa' ||
            raw === 'us' ||
            raw === 'united states of america'
        ) {
            return 'US'
        }

        if (raw.length === 2) {
            return raw.toUpperCase()
        }

        return undefined
    }, [country])

    useEffect(() => {
        setQuery(value || '')
    }, [value])

    useEffect(() => {
        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null

            const clickedInsideInput = !!wrapperRef.current?.contains(target)
            const clickedInsideDropdown = !!dropdownRef.current?.contains(target)

            if (!clickedInsideInput && !clickedInsideDropdown) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handlePointerDown)
        document.addEventListener('touchstart', handlePointerDown)

        return () => {
            document.removeEventListener('mousedown', handlePointerDown)
            document.removeEventListener('touchstart', handlePointerDown)
        }
    }, [])

    useEffect(() => {
        const trimmed = query.trim()

        if (trimmed.length < 2) {
            setResults([])
            setLoading(false)
            setOpen(false)
            return
        }

        let cancelled = false

        const timeoutId = setTimeout(async () => {
            try {
                setLoading(true)

                const data = await autocompleteBankName({
                    query: trimmed,
                    country: normalizedCountry,
                    limit: 10
                })

                if (cancelled) return

                const nextResults = Array.isArray(data)
                    ? data
                    : Array.isArray((data as any)?.message?.data)
                      ? (data as any).message.data
                      : Array.isArray((data as any)?.data)
                        ? (data as any).data
                        : []

                setResults(nextResults)
            } catch {
                if (!cancelled) {
                    setResults([])
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }, 250)

        return () => {
            cancelled = true
            clearTimeout(timeoutId)
        }
    }, [query, normalizedCountry])

    const displayedResults = useMemo(() => {
        const trimmed = query.trim().toLowerCase()

        if (!trimmed) return []

        return results.filter(item =>
            item.bank_name.toLowerCase().includes(trimmed)
        )
    }, [results, query])

    const renderedResults = useMemo(() => {
        if (dropdownPosition?.placement === 'top') {
            return [...displayedResults].reverse()
        }

        return displayedResults
    }, [displayedResults, dropdownPosition?.placement])

    const showDropdown = open && query.trim().length > 0

    useLayoutEffect(() => {
        if (!showDropdown || !wrapperRef.current) {
            setDropdownPosition(null)
            return
        }

        const updatePosition = () => {
            if (!wrapperRef.current) return

            const rect = wrapperRef.current.getBoundingClientRect()

            const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING
            const spaceAbove = rect.top - VIEWPORT_PADDING

            const shouldOpenUp =
                spaceBelow < 220 && spaceAbove > spaceBelow

            const maxHeight = Math.max(
                120,
                Math.min(
                    DEFAULT_MAX_HEIGHT,
                    shouldOpenUp ? spaceAbove - GAP_PX : spaceBelow - GAP_PX
                )
            )

            if (shouldOpenUp) {
                setDropdownPosition({
                    bottom: window.innerHeight - rect.top + GAP_PX,
                    left: rect.left,
                    width: rect.width,
                    maxHeight,
                    placement: 'top'
                })
            } else {
                setDropdownPosition({
                    top: rect.bottom + GAP_PX,
                    left: rect.left,
                    width: rect.width,
                    maxHeight,
                    placement: 'bottom'
                })
            }
        }

        updatePosition()

        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)

        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [showDropdown, displayedResults.length, loading])

    return (
        <AutocompleteField ref={wrapperRef}>
            <FormikInputField
                type='text'
                label={label}
                name={name}
                value={query}
                customClass={customClass}
                externalInputRef={inputRef}
                showRequiredErrors={showRequiredErrors}
                inputProps={{
                    autoComplete: 'off',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const nextValue = e.target.value
                        setQuery(nextValue)
                        onValueChange(nextValue)
                        setOpen(nextValue.trim().length > 0)
                    },
                    onFocus: () => {
                        if (query.trim().length > 0) {
                            setOpen(true)
                        }
                    }
                }}
            />

            {showDropdown &&
                dropdownPosition &&
                createPortal(
                    <SuggestionsDropdown
                        ref={dropdownRef}
                        $placement={dropdownPosition.placement}
                        style={{
                            top: dropdownPosition.top,
                            bottom: dropdownPosition.bottom,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                            maxHeight: dropdownPosition.maxHeight
                        }}
                    >
                        <SuggestionsList $placement={dropdownPosition.placement}>
                            {renderedResults.map(item => (
                                <SuggestionItem
                                    key={`${item.country_code}-${item.bank_name}`}
                                    type='button'
                                    onMouseDown={e => {
                                        e.preventDefault()
                                        setQuery(item.bank_name)
                                        onValueChange(item.bank_name)
                                        onSelect(item)
                                        setOpen(false)
                                        inputRef.current?.blur()
                                    }}
                                >
                                    <SuggestionMain>{item.bank_name}</SuggestionMain>
                                    <SuggestionSecondary>
                                        {item.country_code}
                                    </SuggestionSecondary>
                                </SuggestionItem>
                            ))}

                            {!loading && displayedResults.length === 0 && (
                                <SuggestionEmpty>No matches found</SuggestionEmpty>
                            )}
                        </SuggestionsList>
                    </SuggestionsDropdown>,
                    document.body
                )}
        </AutocompleteField>
    )
}

const AutocompleteField = styled.div`
    position: relative;
    width: 100%;
`

const SuggestionsDropdown = styled.div<{ $placement: 'top' | 'bottom' }>`
    position: fixed;
    background: ${colors.white};
    border: 0.1rem solid #d9dde5;
    border-radius: 1.6rem;
    box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.12);
    overflow-y: auto;
    z-index: 99999;
    display: flex;
    flex-direction: column;

    ${props =>
        props.$placement === 'top' &&
        `
            justify-content: flex-end;
        `}

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        width: 0px;
        height: 0px;
    }

    &:hover {
        scrollbar-width: thin;
    }

    &:hover::-webkit-scrollbar {
        width: 6px;
    }

    &:hover::-webkit-scrollbar-thumb {
        background: rgba(0,0,0,0.25);
        border-radius: 3px;
    }
`

const SuggestionItem = styled.button`
    width: 100%;
    display: block;
    text-align: left;
    background: transparent;
    border: none;
    padding: 1.2rem 1.4rem;
    cursor: pointer;

    &:hover {
        background: #f6f8fb;
    }
`

const SuggestionsList = styled.div<{ $placement: 'top' | 'bottom' }>`
    display: flex;
    flex-direction: column;

    ${props =>
        props.$placement === 'top' &&
        `
            margin-top: auto;
        `}
`

const SuggestionMain = styled.div`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${colors.blueDark};
`

const SuggestionSecondary = styled.div`
    font-size: 1.25rem;
    color: ${colors.grey};
    margin-top: 0.2rem;
`

const SuggestionEmpty = styled.div`
    padding: 1.2rem 1.4rem;
    font-size: 1.3rem;
    color: ${colors.grey};
`